import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { User } from '../_models/user.model';
import { Permission } from '../_models/permission.model';
import { Role } from '../_models/role.model';
import { catchError, filter, map, take } from 'rxjs/operators';
import { HttpUtilsService, QueryParamsModel } from '../../_base/crud';
import { environment } from '../../../../environments/environment';
import { select, Store } from '@ngrx/store';
import { AppState } from '../../reducers';
import { currentAuthToken, currentUser } from '../_selectors/auth.selectors';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { Login, Logout, Reload } from '../_actions/auth.actions';
import { AllCompaniesRequested } from '../../management/_actions/company.actions';
import * as jwt_decode from 'jwt-decode'; // Import jwt_decode library
import { OrganizationService } from '../../bridge-pay/services/organization.service';
import { BridgePayAuthService } from '../../bridge-pay/services/bridgepay-auth.service';
import { RoleTemplate } from '../_models/role-template.model';

const API_URL = environment.USER_BASE;

@Injectable({ providedIn: 'root' })
export class AuthService {
  companyId: number;
  redirectUrl: string;
  currentUserId;
  private user$ = new BehaviorSubject(null);
  private authToken;
  public currentUser: Observable<User>;
  public changed = new BehaviorSubject<boolean>(false);
  private twixorToken;
  private tokenExpirationTimer: any; // Timer to check token expiration
  bridgepayApiPciApiKey: string;
  private bc = new BroadcastChannel('auth_channel');
  private currentToken: string | null = null;
  private userInfo: any = null;
  constructor(
    private http: HttpClient,
    private store: Store<AppState>,
    private httpUtils: HttpUtilsService,
    private snackBar: MatSnackBar,
    private router: Router,
    private route: ActivatedRoute,
    private bridgepayService: OrganizationService,
    private bridgePayAuth: BridgePayAuthService,
  ) {
    this.store.pipe(select(currentAuthToken)).subscribe((res) => {
      this.authToken = res;
      this.initTokenExpirationTimer();
    });
    this.store
      .pipe(
        select(currentUser),
        filter((res) => !!(res && res.id)),
      )
      .subscribe((res) => {
        this.companyId = res.companyID;
        this.user$.next(res);
        this.currentUserId = res.id;
        this.twixorToken = res.twixorToken;

        // Use BridgePay API key from login response (tenant-specific)
        const bridgePayApiKey = res.bridgepayApiKey;

        if (bridgePayApiKey && res.companyUUID) {
          // Initialize BridgePay auth with the tenant's API key - exchange for Bearer token
          this.bridgePayAuth.initializeAuth(bridgePayApiKey, res.companyUUID).subscribe(() => {
            // Now fetch organization to get the PCI API key for Basis Theory
            this.bridgepayService.getDefaultOrganization(res.companyUUID).subscribe((configs) => {
              this.bridgepayApiPciApiKey = configs.data.bridgepay_pci_api_key;
            });
          });
        }
      });
    this.bc = new BroadcastChannel('auth_channel');

    combineLatest([
      this.store.pipe(select(currentAuthToken)),
      this.store.pipe(select(currentUser)),
    ]).subscribe(([token, userInfo]) => {
      this.currentToken = token;
      this.userInfo = userInfo;
    });

    this.bc.onmessage = (event) => {
      const { type, token, userInfo } = event.data;

      switch (type) {
        case 'REQUEST_AUTH':
          if (this.currentToken && this.userInfo) {
            this.bc.postMessage({
              type: 'REPLY_AUTH',
              token: this.currentToken,
              userInfo: this.userInfo,
            });
          }
          break;

        case 'REPLY_AUTH':
          if (token && userInfo && !this.currentToken) {
            this.store.dispatch(new Reload({ authToken: token, userInfo }));
          }
          break;

        case 'LOGOUT':
          this.store.dispatch(new Logout(true, { fromBroadCast: true }));
          break;
      }
    };
  }

  // Initialize the token expiration timer
  private initTokenExpirationTimer() {
    if (this.authToken) {
      const decodedToken = jwt_decode(this.authToken);
      const expirationTime = decodedToken.exp * 1000; // Convert to milliseconds
      const currentTime = new Date().getTime();
      const expiresIn = expirationTime - currentTime;

      this.tokenExpirationTimer = setTimeout(() => {
        this.logout(); // Token has expired, log out the user
        this.router.navigate(['/auth/login']);
      }, expiresIn);
    }
  }

  public getChange(): boolean {
    return this.changed.value;
  }

  public setChanged(chang) {
    return this.changed.next(chang);
  }

  getSession(): Observable<{ accessToken: string; expiresAt: string }> {
    return this.http.get<{ accessToken: string; expiresAt: string }>(
      API_URL + '/api/v2/auth/session',
      { withCredentials: true },
    );
  }

  restoreSession(): Promise<void> {
    localStorage.removeItem('userInfo');
    localStorage.removeItem(environment.authTokenKey);

    return this.store
      .pipe(select(currentAuthToken), take(1))
      .toPromise()
      .then((token) => {
        if (token) {
          return;
        }
        return this.getSession()
          .pipe(catchError(() => of(null)))
          .toPromise()
          .then((session) => {
            if (!session || !session.accessToken) {
              return;
            }
            this.store.dispatch(new Login({ authToken: session.accessToken }));
          });
      });
  }

  public getUser(): Observable<User> {
    return this.user$.asObservable();
  }

  public setUser(user) {
    return this.user$.next(user);
  }

  login(userName: string, password: string): Observable<User> {
    return this.http.post<any>(API_URL + '/login', { userName, password }).pipe(
      map((user) => {
        // store user details and jwt token in local storage to keep user logged in between page refreshes
        if (user.code === 200) {
          user.loginTime = new Date();
          this.store.dispatch(new Login({ authToken: user.accessToken }));
          localStorage.setItem('measurementSystem', user.measurementSystem);
          localStorage.setItem('currentUserPermissions', JSON.stringify(user.permissions));
          if (user && user.typeRole === 'Super Admin') {
            this.store.dispatch(new AllCompaniesRequested());
          }
          return user;
        }
        return;
      }),
    );
  }
  broadcastLogout() {
    this.bc.postMessage({ type: 'LOGOUT' });
    this.currentToken = null;
    this.userInfo = null;
  }
  broadcastNewTab() {
    this.bc.postMessage({ type: 'REQUEST_AUTH' });
  }
  logout(saveURL = true, onSignout?: boolean) {
    this.store.dispatch(new Logout(saveURL, { onSignout }));
  }
  signOut(): Observable<any> {
    return this.http.post(API_URL + `/api/v2/auth/logout`, {});
  }

  getUserByToken(): Observable<User> {
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    return this.http.get<User>(API_URL + '/user-by-token', { headers: httpHeaders }).pipe(
      map((user) => {
        return user;
      }),
    );
  }

  public requestPassword(email: string): Observable<any> {
    return this.http.post(API_URL + '/sendPasswordResetEmail', { email });
  }

  getAllUsers(): Observable<any[]> {
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    return this.http.get<any[]>(API_URL + '/all-user', { headers: httpHeaders });
  }

  getUserById(userId?: number): Observable<User> {
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    return this.http.post<User>(API_URL + '/user-by-id', { id: userId }, { headers: httpHeaders });
  }

  resetPassword(newPassword?: any) {
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    const userTokenForget = this.route.snapshot.queryParams.param1;
    return this.http.post(
      API_URL + '/receiveNewPassword',
      {
        newPassword,
        token: userTokenForget,
      },
      { headers: httpHeaders },
    );
  }

  // Permission
  getAllPermissions(): Observable<Permission[]> {
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    return this.http.get<Permission[]>(API_URL + '/permissions', { headers: httpHeaders });
  }

  // Roles
  getAllRoles(): Observable<Role[]> {
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    return this.http.get<Role[]>(API_URL + '/roles', { headers: httpHeaders });
  }

  getAllUserRoles(): Observable<Role[]> {
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    return this.http.get<Role[]>(API_URL + '/user-roles', { headers: httpHeaders });
  }

  // CREATE =>  POST: add a new role to the server
  createRole(role: Role): Observable<Role> {
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    return this.http.post<Role>(API_URL + '/role', role, { headers: httpHeaders });
  }

  // UPDATE => PUT: update the role on the server
  updateRole(role: Role): Observable<any> {
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    return this.http.put(API_URL + '/u-role', role, { headers: httpHeaders });
  }

  // DELETE => delete the role from the server
  deleteRole(roleId: number): Observable<any> {
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    const body = {
      id: roleId,
    };
    return this.http.put<Role>(API_URL + '/d-role', body, { headers: httpHeaders });
  }

  // DELETE => delete the roles from the server
  deleteRoles(roleIds: number[]): Observable<any> {
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    const body = {
      ids: roleIds,
    };
    return this.http.put<Role>(API_URL + '/d-roles', body, { headers: httpHeaders });
  }

  findRoles(queryParams: QueryParamsModel): Observable<any> {
    // This code imitates server calls
    let sort = queryParams.sortField;

    if (sort === 'department') sort = 'department_name';
    if (sort === 'company') sort = 'company.name';

    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    const body = {
      pageIndex: queryParams.pageNumber,
      pageSize: queryParams.pageSize,
      searchData: queryParams.filter.searchData,
    };

    let fromString = '';

    if (queryParams.filter.searchTitle)
      fromString += `&title_has=${queryParams.filter.searchTitle}`;
    if (queryParams.filter.searchDepartment)
      fromString += `&department_id=${queryParams.filter.searchDepartment}`;
    if (queryParams.filter.searchCompany)
      fromString += `&company_id=${queryParams.filter.searchCompany}`;

    const params = new HttpParams({ fromString });

    return this.http.post<any>(
      API_URL + '/find-roles' + '?sort=' + sort + `,${queryParams.sortOrder}`,
      body,
      { headers: httpHeaders, params },
    );
  }

  getRolesAsCSV(queryParams: QueryParamsModel): Observable<any> {
    const { filter, sortOrder, sortField: originalSortField } = queryParams;

    let sortField = originalSortField;
    if (sortField === 'department') sortField = 'department_name';
    if (sortField === 'company') sortField = 'company.name';

    let fromString = '';

    if (filter.searchData) fromString += `&search_term=${filter.searchData}`;
    if (filter.searchTitle) fromString += `&title_has=${filter.searchTitle}`;
    if (filter.searchDepartment) fromString += `&department_id=${filter.searchDepartment}`;
    if (filter.searchCompany) fromString += `&company_id=${filter.searchCompany}`;

    const params = new HttpParams({ fromString });

    return this.http.get(`${API_URL}/roles/csv?sort=${sortField},${sortOrder}`, {
      params,
      responseType: 'text',
    });
  }

  getQvToken(payload) {
    if (!payload.appId || !payload.userId) {
      return of(null);
    }
    const url = `${environment.QRVEY_ACCESS_MANAGER}/companies/${this.companyId}/qrvey/token?qrvey_user_id=${payload.userId}&&qrvey_app_id=${payload.appId}`;
    return this.http.get<any>(url);
  }

  searchRoles(term: string): Observable<any> {
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    const url = environment.USER_BASE + '/roles/autocomplete?term=' + term;
    return this.http.get<any>(url, { headers: httpHeaders });
  }

  searchRolesByCompany(companyId, term: string): Observable<any> {
    const url = environment.USER_BASE + '/roles/autocomplete?term=' + term + `${companyId}`;
    return this.http.get<any>(url);
  }

  setRedirectUrl(s: string) {
    this.redirectUrl = s;
    localStorage.setItem('redirectUrl', s);
  }

  resetMobilePassword(body) {
    return this.http.post(environment.MOBILE_BASE + '/api/v1/user/reset-password', body);
  }

  checkSSO(email) {
    return this.http.get(environment.USER_BASE + `/sso_check?email=${email}`);
  }

  ssoCallback(code, state) {
    return this.http.get(environment.USER_BASE + `/sso_callback?code=${code}&state=${state}`).pipe(
      map((user: any) => {
        // store user details and jwt token in local storage to keep user logged in between page refreshes
        if (user.code === 200) {
          user.loginTime = new Date();
          this.store.dispatch(new Login({ authToken: user.accessToken }));
          localStorage.setItem('measurementSystem', user.measurementSystem);
          localStorage.setItem('currentUserPermissions', JSON.stringify(user.permissions));
          if (user && user.typeRole === 'Super Admin') {
            this.store.dispatch(new AllCompaniesRequested());
          }
          return user;
        }
        this.router.navigateByUrl('/error/not-signup');
        return;
      }),
    );
  }

  getTwixorDepartments(
    attachmentType: 'ALL' | 'ATTACHED' | 'UNATTACHED',
  ): Observable<{ id: number; name: string }[]> {
    return this.http.get<{ id: number; name: string }[]>(
      `${API_URL}/companies/${this.companyId}/twixor/departments?attachment_type=${attachmentType}`,
    );
  }

  getQRVEYMenu(userId, type) {
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    const url = `${environment.QRVEY_ACCESS_MANAGER}/companies/${
      this.companyId
    }/qrvey/user_access/${userId ? userId : this.currentUserId}?user_type=${type}`;
    return this.http.get<any>(url, { headers: httpHeaders });
  }

  private static getApplicationsQrveyListQueryString(queryParams: QueryParamsModel): string {
    const { pageNumber, pageSize, sortField, sortOrder, filter } = queryParams;
    let fromString = `page=${pageNumber}&size=${pageSize}&sort=${sortField},${sortOrder}`;

    if (filter.searchTerm) {
      fromString += `&search_term=${filter.searchTerm}`;
    } else {
      if (filter.name) fromString += `&name_has=${filter.name}`;
      if (filter.type) fromString += `&type_has=${filter.type}`;
    }

    return fromString;
  }

  getQRVEYApps(queryParams?: QueryParamsModel, id = null): Observable<any> {
    if (!queryParams)
      return this.http.get<any>(
        `${environment.QRVEY_ACCESS_MANAGER}/companies/${this.companyId}/qrvey/applications${
          id ? '/' + id : ''
        }`,
      );
    const params = new HttpParams({
      fromString: AuthService.getApplicationsQrveyListQueryString(queryParams),
    });
    return this.http.get<any>(
      `${environment.QRVEY_ACCESS_MANAGER}/companies/${this.companyId}/qrvey/applications${
        id ? '/' + id : ''
      }`,
      { params },
    );
  }

  createUserAccess(body, userId = null) {
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    const url = `${environment.QRVEY_ACCESS_MANAGER}/companies/${
      this.companyId
    }/qrvey/user_access/${userId ? userId : this.currentUserId}`;
    return this.http.put(url, body, { headers: httpHeaders });
  }

  // Role Templates
  getAllRoleTemplates(): Observable<RoleTemplate[]> {
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    return this.http.get<RoleTemplate[]>(API_URL + '/role-templates', { headers: httpHeaders });
  }

  // CREATE =>  POST: add a new role template to the server
  createRoleTemplate(roleTemplate: RoleTemplate): Observable<RoleTemplate> {
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    return this.http.post<RoleTemplate>(API_URL + '/role-templates', roleTemplate, {
      headers: httpHeaders,
    });
  }

  // UPDATE => PUT: update the role template on the server
  updateRoleTemplate(roleTemplate: RoleTemplate): Observable<any> {
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    return this.http.put(API_URL + '/role-templates/' + roleTemplate.id, roleTemplate, {
      headers: httpHeaders,
    });
  }

  activateRoleTemplate(roleTemplateId: number): Observable<any> {
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    const body = { id: roleTemplateId };

    return this.http.post<RoleTemplate>(
      API_URL + '/role-templates/' + roleTemplateId + '/activate',
      body,
      { headers: httpHeaders },
    );
  }

  // DELETE => delete the role template from the server
  deleteRoleTemplate(roleTemplateId: number): Observable<any> {
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    return this.http.delete<RoleTemplate>(API_URL + '/role-templates/' + roleTemplateId, {
      headers: httpHeaders,
    });
  }

  // DELETE => delete the role templates from the server
  deleteRoleTemplates(roleTemplateIds: number[]): Observable<any> {
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    const body = { ids: roleTemplateIds };
    return this.http.request<RoleTemplate>('delete', API_URL + '/role-templates', {
      body: body,
      headers: httpHeaders,
    });
  }

  findRoleTemplates(queryParams: QueryParamsModel): Observable<any> {
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    const titleHas = queryParams.filter.searchTitle ? queryParams.filter.searchTitle : '';
    const isActive = queryParams.filter.isActive ? queryParams.filter.isActive : '';

    let query = '?';

    query += '&title_has=' + encodeURIComponent(titleHas);
    query += '&is_active=' + isActive;
    query += '&sort=' + queryParams.sortField + `,${queryParams.sortOrder}`;
    query += '&page=' + queryParams.pageNumber;
    query += '&size=' + queryParams.pageSize;
    query += '&q=' + encodeURIComponent(queryParams.filter.searchData);

    const url = API_URL + '/role-templates' + query;

    return this.http.get<any>(url, { headers: httpHeaders });
  }

  searchRoleTemplates(term: string): Observable<any> {
    const url = environment.USER_BASE + '/role-templates/autocomplete?term=' + term;
    return this.http.get<any>(url);
  }
}
